import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CurrencyPipe } from '../../pipes/currency.pipe';
import { PaginationComponent } from '../../pagination/pagination.component';
import { GlobalDateFormatPipe } from '../../../../shared/pipes/global-date-format.pipe';
import type { GlobalDateFormat } from '../../../../shared/pipes/global-date-format.pipe';
import { IconComponentV2 } from '../../icon-component/icon.component';
import { type IconName } from '../../../icons/icon.component';
import { ThemeService } from '../../../../core/services/theme.service';

export interface TableHeader {
  label: string;
  key: string;
  transform?: string;
  dateFormat?: GlobalDateFormat;
  align?: 'text-left' | 'text-right' | 'text-center';
  headerAlign?: 'text-left' | 'text-right' | 'text-center';
  isPrevious?: boolean;
  showProgress?: boolean;
  showTooltip?: boolean;
  showIcon?: boolean;
  growthKey?: string;
  sortable?: boolean;
  isSortIconShow?: boolean;
  cellType?: 'badge' | 'pill';
  cellClass?: string;
  isHtml?: boolean;
  subKey?: string;
  subTransform?: string;
  subDateFormat?: GlobalDateFormat;
  noTransform?: boolean;
  renderAsHtml?: boolean;
  colClass?: string;
}

export type PixelValue = `${number}px`;
type colorCode = `#${string}`;

export interface TableActionOptions {
  columnLabel?: string;
  showToggleButton?: boolean;
  headingColor?: colorCode;
  iconPath?: string;
  showIcon?: boolean;
  fontSize?: PixelValue;
  toggleOptions?: {
    isButton?: boolean;
    label?: string;
    icon?: string;
    isShow?: boolean;
    visibilityKey?: string;
    iconName: any;
    color?: string;
    value?: string | number;
    tooltip?: string;
  }[];
  useIconV2?: boolean;
}

@Component({
  selector: 'app-paginated-table',
  standalone: true,
  templateUrl: './paginated-table.component.html',
  styleUrl: './paginated-table.component.scss',
  providers: [CurrencyPipe, DatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, PaginationComponent, IconComponentV2],
})
export class PaginatedTableComponent implements OnChanges {
  @ViewChild('tableTooltip', { static: false })
  tooltipRef!: ElementRef<HTMLDivElement>;

  private themeService = inject(ThemeService);
  Math = Math;

  @Input() isCheckboxShow: boolean = false;
  /** When set, checkbox selection is tracked by id (e.g. page-index) instead of row object reference. */
  @Input() getRowSelectionId?: (row: any, index: number) => string | number | null;
  @Input() set selection(value: any[]) {
    if (this.selectionUsesIds()) {
      this.syncSelectionIdsFromRows(value || []);
      return;
    }
    const newSet = new Set(value || []);
    let match = newSet.size === this.selectedRows.size;
    if (match) {
      for (const item of newSet) {
        if (!this.selectedRows.has(item)) {
          match = false;
          break;
        }
      }
    }
    if (!match) {
      this.selectedRows = newSet;
    }
  }
  @Input() isRowSelectable: (row: any) => boolean = () => true;
  @Input() hidePagination: boolean = false;
  @Input() tableActionOptions: TableActionOptions = {
    showToggleButton: false,
    headingColor: '#131921',
    iconPath: '',
    fontSize: '16px',
    toggleOptions: [],
  };
  @Input() tableData?: any[] | null = null;
  @Input() totalItems: number = 0;
  /** Optional server-side page override when `totalItems` is unavailable. */
  @Input() pageCount?: number | null;
  @Input() serverSide: boolean = false;
  @Input() previousData: any[] | null = null;
  @Input() tooltipData: { key: string; label: string }[] = [];
  @Input() headers: TableHeader[] | null = null;
  @Input() tableKey: string = '';
  @Input() headerClass: string = '';
  @Input() tableWrapperClass: string = '';
  @Input() tableClass: string = '';
  @Input() containerClass: string = 'overflow-x-auto';
  @Input() backdropBlurClass: string = 'backdrop-blur-xl';
  @Input() className: string = '';
  @Input() showSeriesNo: boolean = false;
  @Input() headerBgColor?: { light?: string; dark?: string };
  @Input() headerTextColor?: { light?: string; dark?: string };
  @Input() sortIconColor?: { light?: string; dark?: string };
  @Input() hidePaginationTopBorder: boolean = true;
  @Input() paddingXAxis: string = 'px-0 sm:px-1 md:px-2';
  @Input() paddingYAxis: string = 'py-3';
  @Input() cellTemplates: { [key: string]: TemplateRef<any> } = {};
  @Input() hideOuterBorder: boolean = false;
  @Input() headerBorderClass: string = 'border-b border-[#EAEAEA] dark:border-line';
  @Input() rowBorderClass: string = 'border-[#EAEAEA] dark:border-line';
  @Input() headerDefaultBgClass: string = 'bg-[#F5F5F5] dark:bg-surface-3';
  @Input() headerRoundedClass: string = 'first:rounded-tl-[14px] last:rounded-tr-[14px]';
  @Input() cellPaddingClass: string = 'py-3 px-1 sm:px-2 md:px-3';

  @Output() fetchData = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() actionSelected = new EventEmitter<{ action: string; row: any }>();
  isDarkTheme = computed(() => this.themeService.theme() === 'dark');

  private globalDatePipe = new GlobalDateFormatPipe();

  constructor(private currencyPipe: CurrencyPipe, private datePipe: DatePipe) { }

  sortState: { [tableKey: string]: { key: string; order: 'asc' | 'desc' } } =
    {};
  tooltipHTML = '';
  finalValue: { type: string; amount: number; percent: string | number } = {
    percent: '0',
    type: '',
    amount: 0,
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tableData'] || changes['totalItems'] || changes['pageSize'] || changes['pageCount']) {
      this.calculateTotalPages();

      // Reset to page 1 for client-side pagination when data source changes
      if (changes['tableData'] && !this.serverSide) {
        this.currentPage = 1;
      }
    }

    const progressHeader = this.headers?.find((h) => h.showProgress);
    if (progressHeader) {
      this.getTotalGrowth(progressHeader.key);
    }
  }

  @Input() currentPage = 1;
  @Input() pageSize = 10;
  totalPages = 0;
  pageSizes = [5, 10, 15, 20];

  ngOnInit() {
    this.calculateTotalPages();
  }

  calculateTotalPages() {
    const items = this.serverSide ? this.totalItems || 0 : this.tableData?.length ?? 0;

    if (items > 0 && this.pageSize > 0) {
      this.totalPages = Math.ceil(items / this.pageSize);
      return;
    }

    if (
      this.serverSide &&
      this.pageCount != null &&
      Number.isFinite(this.pageCount) &&
      this.pageCount >= 0
    ) {
      this.totalPages = this.pageCount;
      return;
    }

    this.totalPages = 0;
  }

  get totalPagesArray() {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get paginatedData() {
    // Server-side: parent provides the current page rows. Client-side: slice full dataset.
    if (this.serverSide) return this.tableData ?? [];

    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.tableData?.slice(startIndex, startIndex + this.pageSize);
  }

  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;

      // notify parent so it can fetch server-side page when in serverSide mode
      if (this.serverSide) this.pageChange.emit(this.currentPage);
    }
  }

  changePageSize(size: number) {
    this.pageSize = size;
    this.currentPage = 1;
    if (this.serverSide) this.pageSizeChange.emit(this.pageSize);
    else this.calculateTotalPages();
  }

  /** Get value from row with transformations */
  getValue(
    key: string,
    data: any,
    index: number,
    transform?: string,
    header?: TableHeader
  ): any {
    if (!key) return '';
    const row = Array.isArray(data) ? data[index] : data;
    if (!row || row[key] == null || row[key] === 'undefined') return 0;

    if (key.toLowerCase().includes('amount')) {
      return this.currencyPipe.transform(row[key]);
    }

    if (
      key.toLowerCase().includes('start') ||
      key.toLowerCase().includes('date') ||
      header?.dateFormat
    ) {
      // Use the new global date format pipe with custom dateFormat or fallback to transform
      const dateFormat =
        header?.dateFormat || (transform as GlobalDateFormat) || 'shortDate';
      return this.globalDatePipe.transform(row[key], dateFormat);
    }

    if (transform === 'number') {
      const val = Number(row[key]);
      return isNaN(val) ? row[key] : val.toLocaleString('en-US');
    }

    return row[key];
  }

  /** Get correct row (current vs previous) */
  resolveRow(h: TableHeader, row: any, index: number): any {
    return h.isPrevious && this.previousData?.length
      ? this.previousData[index]
      : row;
  }

  /** Get value from subKey with transformations */
  getSubValue(h: TableHeader, row: any, i: number): string {
    if (!h.subKey) return '';
    const subHeader: TableHeader = {
      ...h,
      key: h.subKey,
      transform: h.subTransform || h.transform,
      dateFormat: h.subDateFormat || h.dateFormat,
    };
    return this.getValue(
      h.subKey,
      this.resolveRow(h, row, i),
      i,
      subHeader.transform,
      subHeader
    );
  }

  toggleView(row: any) {
    this.fetchData.emit(row);
  }

  /** Growth calculation */
  private getTotalGrowth(key: string) {
    const sum = (arr?: any[]) =>
      arr?.reduce(
        (curr, acc) => curr + (acc[key] && acc[key] !== 'N.A.' ? +acc[key] : 0),
        0
      ) ?? 0;

    this.finalValue = this.calculateProfitLoss(
      sum((this.tableData || []) as any[]),
      sum((this.previousData || []) as any[])
    );
  }

  private calculateProfitLoss(a: number, b: number) {
    if (b < a) {
      const profit = a - b;
      let value = {
        type: 'Profit',
        amount: profit,
        percent: '+' + (b == 0 ? profit : ((profit / b) * 100).toFixed(2)),
      };

      if (String(value.percent) == '-Infinity')
        value.percent = '+' + String(profit);

      return value;
    } else if (b > a) {
      const loss = b - a;
      let value = {
        type: 'Loss',
        amount: loss,
        percent: '-' + (b == 0 ? loss : ((loss / b) * 100).toFixed(2)),
      };

      if (value.percent == '-Infinity') value.percent = '-' + String(loss);

      return value;
    }
    return { type: 'No Profit No Loss', amount: 0, percent: 0 };
  }

  /** Tooltip handling */
  triggerTooltip(event: MouseEvent, header: TableHeader, row: any) {
    if (
      !header.showTooltip ||
      !row ||
      !(this.tooltipData && this.tooltipData.length)
    )
      return;

    this.tooltipHTML = this.tooltipData
      .map(
        (el) => `
        <div class="flex items-center justify-between gap-2">
          <span class="mr-3 text-[#B6BCC4] text-base">${el.label}:</span>
          <span class="text-white">${row[el.key] ?? '-'}</span>
        </div>`
      )
      .join('');

    this.tooltipRef.nativeElement.classList.remove('hidden');
    this.showTooltip(event);
  }

  showTooltip(event: MouseEvent) {
    const tooltipEl = this.tooltipRef.nativeElement;
    const parentRect = tooltipEl.parentElement?.getBoundingClientRect();
    if (!parentRect) return;

    const { offsetWidth: w, offsetHeight: h } = tooltipEl;
    const offset = 20;
    const margin = 15;

    let left = event.layerX + offset;
    let top = event.layerY + offset;

    if (event.layerX + w + offset > parentRect.width)
      left = event.layerX - w - margin;
    if (event.layerY + h > parentRect.height) top = event.layerY - h + offset;

    left = Math.max(margin, Math.min(left, parentRect.width - w - margin));
    top = Math.max(margin, Math.min(top, parentRect.height - h));

    Object.assign(tooltipEl.style, {
      position: 'absolute',
      left: `${left}px`,
      top: `${top}px`,
    });
  }

  hideTooltip() {
    this.tooltipRef.nativeElement.classList.add('hidden');
  }

  getProgressForRow(value1: any, value2: any, key: any) {
    if (!value1 || !value2 || !key) return [];
    return [this.calculateProfitLoss(value1[key], value2[key])];
  }

  /* checkbox selection */

  selectedRows = new Set<any>();
  selectedRowIds = new Set<string | number>();

  private selectionUsesIds(): boolean {
    return typeof this.getRowSelectionId === 'function';
  }

  private resolveRowSelectionId(row: any, index: number): string | number | null {
    const id = this.getRowSelectionId?.(row, index);
    if (id === undefined || id === null || id === '') {
      return null;
    }
    return id;
  }

  private syncSelectionIdsFromRows(rows: any[]): void {
    const data = this.tableData ?? [];
    const newSet = new Set<string | number>();
    for (const row of rows) {
      const idx = data.indexOf(row);
      if (idx < 0) continue;
      const id = this.resolveRowSelectionId(row, idx);
      if (id != null) newSet.add(id);
    }
    if (!this.selectionIdSetsEqual(newSet, this.selectedRowIds)) {
      this.selectedRowIds = newSet;
    }
  }

  private selectionIdSetsEqual(a: Set<string | number>, b: Set<string | number>): boolean {
    if (a.size !== b.size) return false;
    for (const id of a) {
      if (!b.has(id)) return false;
    }
    return true;
  }

  isRowSelected(row: any, index: number): boolean {
    if (this.selectionUsesIds()) {
      const id = this.resolveRowSelectionId(row, index);
      return id != null && this.selectedRowIds.has(id);
    }
    return this.selectedRows.has(row);
  }

  toggleRowSelection(row: any, index: number, event: Event): void {
    event.stopPropagation();
    if (!this.isRowSelectable(row)) return;
    if (this.selectionUsesIds()) {
      const id = this.resolveRowSelectionId(row, index);
      if (id == null) return;
      if (this.selectedRowIds.has(id)) {
        this.selectedRowIds.delete(id);
      } else {
        this.selectedRowIds.add(id);
      }
      this.selectionChange.emit(this.getSelectedRows());
      return;
    }
    if (this.selectedRows.has(row)) {
      this.selectedRows.delete(row);
    } else {
      this.selectedRows.add(row);
    }
    this.selectionChange.emit(this.getSelectedRows());
  }

  getSelectableRows(): any[] {
    return this.tableData?.filter((row) => this.isRowSelectable(row)) ?? [];
  }

  hasSelectableRows(): boolean {
    return this.getSelectableRows().length > 0;
  }

  isAllSelected(): boolean {
    const selectableRows = this.getSelectableRowsWithIndex();
    return (
      selectableRows.length > 0 &&
      selectableRows.every(({ row, index }) => this.isRowSelected(row, index))
    );
  }

  isSomeSelected(): boolean {
    const selectableRows = this.getSelectableRowsWithIndex();
    const selectedSelectableCount = selectableRows.filter(({ row, index }) =>
      this.isRowSelected(row, index)
    ).length;
    return (
      selectedSelectableCount > 0 &&
      selectedSelectableCount < selectableRows.length
    );
  }

  toggleAllRows(event: Event): void {
    if (!this.hasSelectableRows()) return;

    const checkbox = event.target as HTMLInputElement;
    const selectableRows = this.getSelectableRowsWithIndex();
    if (this.selectionUsesIds()) {
      if (checkbox.checked) {
        for (const { row, index } of selectableRows) {
          const id = this.resolveRowSelectionId(row, index);
          if (id != null) this.selectedRowIds.add(id);
        }
      } else {
        for (const { row, index } of this.getRowsWithIndex()) {
          const id = this.resolveRowSelectionId(row, index);
          if (id != null) this.selectedRowIds.delete(id);
        }
      }
      this.selectionChange.emit(this.getSelectedRows());
      return;
    }

    if (checkbox.checked) {
      selectableRows.forEach(({ row }) => this.selectedRows.add(row));
    } else {
      this.tableData?.forEach((row) => this.selectedRows.delete(row));
    }
    this.selectionChange.emit(this.getSelectedRows());
  }

  getSelectedRows(): any[] {
    if (this.selectionUsesIds()) {
      return this.getRowsWithIndex()
        .filter(({ row, index }) => this.isRowSelected(row, index))
        .map(({ row }) => row);
    }
    return Array.from(this.selectedRows);
  }

  clearSelection(): void {
    this.selectedRows.clear();
    this.selectedRowIds.clear();
    this.selectionChange.emit([]);
  }

  private getRowsWithIndex(): Array<{ row: any; index: number }> {
    return (this.tableData ?? []).map((row, index) => ({ row, index }));
  }

  private getSelectableRowsWithIndex(): Array<{ row: any; index: number }> {
    return this.getRowsWithIndex().filter(({ row }) => this.isRowSelectable(row));
  }

  getSelectedCount(): number {
    return this.selectedRows.size;
  }

  sortData(array: any[], key: string, tableKey: string) {
    if (!array?.length) return;

    // Normalize key comparison (case-insensitive)
    let normalizedKey = Object.keys(array[0]).find(
      (k) => k.toLowerCase() === key.toLowerCase()
    );

    if (!normalizedKey) return; // No matching key found

    // Check if sorting is enabled for this column
    const header = this.headers?.find((h) => h.key === normalizedKey);
    if (header && header.sortable === false) {
      return; // Do not sort if sortable is explicitly false
    }

    let current = this.sortState[tableKey];
    const newOrder =
      current && current.key === normalizedKey && current.order === 'asc'
        ? 'desc'
        : 'asc';
    this.sortState[tableKey] = { key: normalizedKey, order: newOrder };

    array.sort((a, b) => {
      let valA = a[normalizedKey] ?? '';
      let valB = b[normalizedKey] ?? '';

      // Try parsing as number with common symbols removed
      const numA =
        typeof valA === 'string'
          ? parseFloat(valA.replace(/[%$,]/g, ''))
          : valA;
      const numB =
        typeof valB === 'string'
          ? parseFloat(valB.replace(/[%$,]/g, ''))
          : valB;

      if (
        typeof numA === 'number' &&
        typeof numB === 'number' &&
        !isNaN(numA) &&
        !isNaN(numB)
      ) {
        return newOrder === 'asc' ? numA - numB : numB - numA;
      }

      return newOrder === 'asc'
        ? valA.toString().localeCompare(valB.toString())
        : valB.toString().localeCompare(valA.toString());
    });
  }

  openMenuIndex: number | null = null;

  toggleMenu(index: number) {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  selectAction(option: any, row: any, event?: Event) {
    if (event) event.stopPropagation();
    this.openMenuIndex = null; // close menu
    this.actionSelected.emit({
      action: option.value,
      row,
    });
  }

  getHeaderAlignClass(header: TableHeader): string {
    if (header.headerAlign) {
      return header.headerAlign;
    }
    if (header.key === 'action') {
      return header.align || 'text-center';
    }
    return 'text-left';
  }

  getHeaderJustifyClass(header: TableHeader): string {
    const align = this.getHeaderAlignClass(header);
    if (align === 'text-center') {
      return 'justify-center';
    }
    if (align === 'text-right') {
      return 'justify-end';
    }
    return 'justify-start';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // If clicked outside the menu and outside the ellipsis button
    if (!target.closest('.action-menu') && !target.closest('.ellipsis-btn')) {
      this.openMenuIndex = null;
    }
  }
}
